import Dialog from '@euler/components/dialog';
import { kDefaultOkButtonText } from '@euler/functions/constants';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { delayed } from '@euler/utils';
import { usePersistFn } from '@euler/utils/hooks';
import { useNavigation } from '@react-navigation/native';
import { useRef, useState } from 'react';
import { AlertButton, AlertOptions } from 'react-native';

export type AlertScreenProps = {
  title: string;
  message?: string;
  okButtonText?: string;
  onOk?: () => void;
  onClosed?: () => void;
  buttons?: AlertButton[];
  options?: AlertOptions;
  verticalButtons?: boolean;
};

export const AlertScreen = wrapNavigatorScreen(
  ({
    title,
    message,
    okButtonText = kDefaultOkButtonText,
    onOk,
    onClosed,
    verticalButtons,
    ...props
  }: AlertScreenProps) => {
    const buttons = props.buttons ?? [{ text: okButtonText, onPress: onOk }];

    const navigation = useNavigation();
    const [visible, setVisible] = useState(true);

    const nextAction = useRef<() => void>();

    const close = usePersistFn((callback?: () => void) => {
      setVisible(false);
      nextAction.current = callback;
    });

    const onClose = usePersistFn(() => {
      close();
    });

    const onDismiss = usePersistFn(() => {
      navigation.goBack();
      delayed(() => {
        onClosed?.();
        nextAction.current?.();
        nextAction.current = undefined;
      }, 100);
    });

    return (
      <Dialog.Container
        visible={visible}
        useNativeDriver
        verticalButtons={verticalButtons ?? buttons.length > 2}
        onRequestClose={onClose}
        onDismiss={onDismiss}
      >
        <Dialog.Title>{title}</Dialog.Title>
        {message ? <Dialog.Description>{message}</Dialog.Description> : null}
        {buttons.map((button, index) => (
          <Dialog.Button
            key={index}
            label={button.text}
            destructive={button.style === 'destructive'}
            onPress={() => {
              close(button.onPress);
            }}
          />
        ))}
      </Dialog.Container>
    );
  },
  {
    headerShown: false,
    animationEnabled: false,
    cardStyle: { backgroundColor: 'transparent' },
    presentation: 'transparentModal',
  },
);
